import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DocumentService } from '@app/shared/services';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { RootStoreState } from '..';
import * as selectors from '../../root-store/vault-store/vault.selector';
import * as vaultActions from './actions';
import * as loginSelectors from '../../root-store/login-store/login.selector';

@Injectable()
export class VaultStoreEffects {
  user;
  forms;
  subscription = new Subscription();

  constructor(
    private documentService: DocumentService,
    private sharedVaultSrevice: SharedVaultService,
    private actions$: Actions,
    private store: Store,
    private store$: Store<RootStoreState.State>,
    private router: Router
  ) {
    this.subscription.add(
      this.store.pipe(select(loginSelectors.getCurrentUserFullDetails)).subscribe((result) => {
        if (result) {
          this.user = result;
        }
      })
    );
    this.subscription.add(
      this.store.pipe(select(selectors.getcurrentForms())).subscribe((result) => {
        this.forms = result;
      })
    );
  }

  vaultObject = this.store$.select('Vault');

  getSharedVault$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.GetVaultRequestAction>(vaultActions.ActionTypes.GET_VAULT_REQUEST),
      switchMap((action: any) => {
        const userId = action.userId || this.user.id;
        return this.sharedVaultSrevice.getVaults(userId, true, action.loader).pipe(
          map((data) => {
            return new vaultActions.GetVaultSuccessAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.GetVaultFailureAction(error));
          })
        );
      })
    )
  );

  getOfficeSharedVault$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.getOfficeStateRequest>(vaultActions.ActionTypes.OFFICE_STATS_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getOfficeAdminList(action.payload).pipe(
          map((data) => {
            return new vaultActions.officeStateAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.OfficeFailureAction(error));
          })
        );
      })
    )
  );

  getSharedWithMeFolders$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.UserFoldersSharedWithMeRequest>(vaultActions.ActionTypes.USER_FOLDERS_SHARED_WITH_ME_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getSharedWithMeFolders().pipe(
          map((data) => {
            return new vaultActions.UserFoldersSharedWithMeAction(data.data);
          }),

          catchError((error) => {
            return of(new vaultActions.UserFoldersSharedWithMeFailureAction(error));
          })
        );
      })
    )
  );

  removeUserDefaultFolder$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.RemoveDefaultFolderRequest>(vaultActions.ActionTypes.REMOVE_DEFAULT_FOLDER_REQUEST),
      switchMap((action: any) => {
        return this.documentService.removeDefaultVaultFolders(action.payload).pipe(
          map((data) => {
            const model = {
              default: data.default,
              userFolder: data.userFolder,
              allDefaultFolders: data.defaultAll,
              sharedWithMe: data.sharedFolder,
            };
            return new vaultActions.RemoveDefaultFolderAction(model);
          }),

          catchError((error) => {
            return of(new vaultActions.RemoveDefaultFolderFailureAction(error));
          })
        );
      })
    )
  );

  addUserDefaultFolder$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.AddDefaultFolderRequest>(vaultActions.ActionTypes.ADD_DEFAULT_FOLDER_REQUEST),
      switchMap((action: any) => {
        return this.documentService.addDefaultVaultFolders(action.payload).pipe(
          map((data) => {
            const model = {
              default: data.default,
              userFolder: data.userFolder,
              allDefaultFolders: data.defaultAll,
              sharedWithMe: data.sharedFolder,
            };
            this.router.navigate([`vault/folder/${data.vaultUserFolder.id}`]);

            return new vaultActions.AddDefaultFolderAction(model);
          }),
          catchError((error) => {
            return of(new vaultActions.AddDefaultFolderFailureAction(error));
          })
        );
      })
    )
  );

  removeDefaultFolderFile$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.RemoveDefaultFolderFileAction>(vaultActions.ActionTypes.REMOVE_DEFAULT_FOLDER_FILE),
      switchMap((action: any) => {
        return this.documentService.removeVaultFolderFile(action.payload).pipe(
          map((data) => {
            return new vaultActions.RemoveDefaultFolderFileAction(data.data);
          }),
          catchError((error) => {
            return of(new vaultActions.RemoveDefaultFolderFileFailureAction(error));
          })
        );
      })
    )
  );

  personalFolderFiles$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.PersonalFolderFilesRequest>(vaultActions.ActionTypes.PERSONAL_FOLDER_FILES_REQUEST),
      switchMap((action: any) => {
        const { userId, folderId, mydata } = action.payload;

        return this.documentService.getPersonalFiles(userId, folderId, mydata).pipe(
          map((data) => {
            return new vaultActions.PersonalFolderFiles(data.data);
          }),
          catchError((error) => {
            return of(new vaultActions.PersonalFolderFiles([]));
          })
        );
      })
    )
  );

  moveFileToFolder$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.MoveFiletoFolderRequest>(vaultActions.ActionTypes.MOVE_FILE_TO_FOLDER_REQUEST),
      switchMap((action: any) => {
        return this.documentService.moveFileToFolder(action.payload).pipe(
          map((data) => {
            const model = {
              folders: {
                default: data.default,
                userFolder: data.userFolder,
                allDefaultFolders: data.defaultAll,
                sharedWithMe: data.sharedFolder,
              },
              personalFiles: data.filesInFolder,
            };

            return new vaultActions.MoveFileToFolderAction(model);
          }),
          catchError((error) => {
            return of(new vaultActions.MoveFileToFolderAction(error));
          })
        );
      })
    )
  );

  // sharedDataWithMe$: Observable<any> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType<vaultActions.SharedDataWithMeRequest>(vaultActions.ActionTypes.SHARED_DATA_WITH_ME_REQUEST),
  //     switchMap((action: any) => {
  //       return this.documentService.getSharedDataWithMe(action.payload.userId, action.payload.sharingUserId).pipe(
  //         map((data) => {
  //           const model = {
  //             sharedData: {
  //               sharedFolders: data.sharedFolders,
  //               sharedFiles: data.sharedFiles,
  //               estate: data.estate,
  //               assetForms: data.assetForms,
  //               sharedDataList: data.sharedData,
  //             },
  //           };

  //           return new vaultActions.SharedDataWithMeAction(model);
  //         }),
  //         catchError((error) => {
  //           return of(new vaultActions.SharedDataWithMeFailureAction(error));
  //         })
  //       );
  //     })
  //   )
  // );

  deleteFile$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.DeleteFileRequest>(vaultActions.ActionTypes.DELETE_FILE_REQUEST),
      switchMap((action: any) => {
        const { userId, fileId } = action.payload;

        return this.documentService.deleteVaultFile(userId, fileId).pipe(
          map((data) => {
            return new vaultActions.DeleteFileAction(data.data);
          }),
          catchError((error) => {
            return of(new vaultActions.DeleteFileFailureAction(error));
          })
        );
      })
    )
  );

  deleteFolder$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.DeleteFolderRequest>(vaultActions.ActionTypes.DELETE_FOLDER_REQUEST),
      switchMap((action: any) => {
        const { userId, folderId } = action.payload;

        return this.documentService.deleteVaultFolder(userId, folderId).pipe(
          map((data) => {
            const model = {
              default: data.default,
              userFolder: data.userFolder,
              allDefaultFolders: data.defaultAll,
              sharedWithMe: data.sharedFolder,
            };

            return new vaultActions.DeleteFolderAction(model);
          }),
          catchError((error) => {
            return of(new vaultActions.DeleteFolderFailureAction(error));
          })
        );
      })
    )
  );

  vaultAdminAccess$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.AdminAccessRequest>(vaultActions.ActionTypes.ADMIN_ACCESS_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getAdminAccess().pipe(
          map((data) => {
            return new vaultActions.AdminAccessAction(data.data);
          }),
          catchError((error) => {
            return of(new vaultActions.AdminAccessFailureAction(error));
          })
        );
      })
    )
  );

  filesInFolders$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.SharedFilesInFolderRequest>(vaultActions.ActionTypes.SHARED_FILES_IN_FOLDERS_WITH_ME_REQUEST),
      switchMap((action: any) => {
        const { userId, sharingUserId, vaultUserFolderId } = action.payload;

        return this.documentService.getSharedFilesinFolders(userId, sharingUserId, vaultUserFolderId).pipe(
          map((data) => {
            return new vaultActions.SharedFilesInFolderAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.SharedFilesInFolderFailureAction(error));
          })
        );
      })
    )
  );

  vaultForms$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.FormRequest>(vaultActions.ActionTypes.FORMS_REQUEST),
      switchMap((action: any) => {
        if (this.forms?.length) {
          return this.store.select(selectors.getcurrentForms()).pipe(
            map((res) => {
              return new vaultActions.FormAction(this.forms);
            })
          );
        } else {
          return this.documentService.getFolderForms2().pipe(
            map((data) => {
              return new vaultActions.FormAction(data);
            }),
            catchError((error) => {
              return of(new vaultActions.FormFailureAction(error));
            })
          );
        }
      })
    )
  );

  categories$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.CategoriesRequest>(vaultActions.ActionTypes.CATEGORIES_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getVaultCategories().pipe(
          map((data) => {
            return new vaultActions.CategoriesAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.CategoriesFailureAction(error));
          })
        );
      })
    )
  );

  vaultList$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.vaultStateAction>(vaultActions.ActionTypes.VAULT_STATS_REQUEST),
      switchMap((action: any) => {
        return this.store.select(selectors.getcurrentVaultUserId()).pipe(
          mergeMap((data) => {
            if (data != null) {
              return this.documentService.getUslefileListCount(data).pipe(
                map((data) => {
                  return new vaultActions.vaultStateAction(data);
                }),
                catchError((error) => {
                  return of(new vaultActions.vaultStateFailureAction(error));
                })
              );
            } else {
              return [];
            }
          }),
          catchError((error) => {
            return of(new vaultActions.vaultStateFailureAction(error));
          })
        );
      })
    )
  );

  getSharedDataWithMe$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.SharedDataWithMeRequest>(vaultActions.ActionTypes.SHARED_DATA_WITH_ME_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getSharedWithMeFolders().pipe(
          map((data) => {
            return new vaultActions.SharedDataWithMeAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.SharedDataWithMeFailureAction(error));
          })
        );
      })
    )
  );

  getSharedDataByMe$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType<vaultActions.SharedDataByMeRequest>(vaultActions.ActionTypes.SHARED_DATA_BY_ME_REQUEST),
      switchMap((action: any) => {
        return this.documentService.getSharedByme().pipe(
          map((data) => {
            return new vaultActions.SharedDataByMeAction(data);
          }),
          catchError((error) => {
            return of(new vaultActions.SharedDataByMeFailureAction(error));
          })
        );
      })
    )
  );
}
